import { lookupByAirportCode } from './airport_code_aliases.js';
import { AIRPORT_STATES_US } from './airport_states_us.js';

/**
 * Geo reference: the USPS state/territory code of a US airport (`"PWM"` → `"ME"`).
 *
 * Why this exists (#4135): the IATA→city authority names `PWM` and `PDX` both "Portland", so
 * the DE/PARA combobox showed two identical rows for airports ~5h30 of flight apart. Booking
 * sites label US airports `City, ST`; this is the `ST`. The table comes from the same public
 * dataset (mwgg/Airports) that feeds `airport_cities_world`, picking the same entry per IATA.
 *
 * Display only: `cityByAirport` stays the bare city, because the offer filter groups by it.
 *
 * Pure (no IO): a compile-time table plus the curated corrections below.
 */

// A leading 3-letter IATA code, optionally followed by descriptive text the source appends
// (e.g. "PWM - PORTLAND"). Same shape as `airportCoord`.
const LEADING_IATA = /^\s*([A-Za-z]{3})\b/;

/** Where the dataset's state is not the one travelers read. DCA and IAD sit in Virginia, but
 *  they serve Washington and every booking site labels them "Washington, DC". */
const CURATED: Readonly<Record<string, string>> = { DCA: 'DC', IAD: 'DC' };

/**
 * The USPS code for a US airport or US-territory airport, or `null` for any other airport, an
 * unknown code, or text that does not start with an IATA code. Accepts a bare code (`"PWM"`)
 * or the source's IATA-prefixed display string (`"PWM - PORTLAND"`).
 */
export function stateByAirport(airport: string | null | undefined): string | null {
  if (!airport) return null;
  const code = LEADING_IATA.exec(airport)?.[1]?.toUpperCase();
  if (!code) return null;
  return lookupByAirportCode(code, (iata) => CURATED[iata] ?? AIRPORT_STATES_US[iata]);
}
